/*
 * Tool 显示元数据解析（1:1 对齐 XHeyClaw-Web/lib/tool-display.ts）
 *
 * 后端 openclaw_proxy 在 SSE 流里已经把 function.name 替换成中文 label
 *（如"执行命令"、"读取文件"），前端只需从 args 里 parse 出动态后缀 detail
 *（exec intent / 文件 basename / 关键词等业务语义短语）。
 *
 * 注：工具时间线骨架改版后每步不再渲染 tool icon，原 LABEL_TO_ICON /
 * ICON_COMPONENTS 反向表与 icon 字段已移除，resolveToolDisplay 只返回
 * { label, detail }。
 */

import { getSkillDisplayName } from './skillCache'

/* ──────────────── 动态 detail 提取 ──────────────── */

function asRecord(args: unknown): Record<string, unknown> | undefined {
  return args && typeof args === 'object'
    ? (args as Record<string, unknown>)
    : undefined
}

/** 从 args 的 path/file_path/filepath/filename 字段提取 basename */
function detailFileBasename(args: unknown): string | undefined {
  const rec = asRecord(args)
  if (!rec) return undefined
  const raw =
    rec.path ??
    rec.file_path ??
    rec.filepath ??
    rec.filename ??
    rec.xlsx_path ??
    rec.docx_path ??
    rec.word_path ??
    rec.pdf_path ??
    rec.source_path ??
    rec.output_path
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  const segs = trimmed.split(/[\\/]/).filter(Boolean)
  return segs[segs.length - 1] || undefined
}

/** 检测 args 是否是读取 .../skills/{slug}/SKILL.md */
function detectSkillLoad(args: unknown): { slug: string } | null {
  const rec = asRecord(args)
  if (!rec) return null
  const raw = rec.path ?? rec.file_path ?? rec.filepath ?? rec.filename
  if (typeof raw !== 'string') return null
  const segs = raw.trim().split(/[\\/]/).filter(Boolean)
  const last = segs[segs.length - 1]
  if (last !== 'SKILL.md') return null
  const skillsIdx = segs.lastIndexOf('skills')
  if (skillsIdx >= 0 && skillsIdx + 1 < segs.length - 1) {
    const slug = segs[skillsIdx + 1]
    if (slug) return { slug }
  }
  return null
}

/** xhey CLI 子命令 → 业务语义短语 */
const XHEY_SUBCOMMAND_PHRASES: Record<string, string> = {
  attendance: '查询考勤',
  customer: '查询客户',
  photo: '查询照片',
  report: '生成报告',
  user: '查询成员',
  team: '团队架构',
  tag: '查询标签',
  stats: '数据统计',
  watermark: '查询水印',
  oss: '文件存储',
  folder: '查询相册',
  verify: '照片验真',
  log: '操作日志',
  image: '照片信息',
  auth: '凭证管理',
  help: '查看帮助'
}

const KNOWN_XHEY_SUBS = Object.keys(XHEY_SUBCOMMAND_PHRASES)

/** 常见系统命令 → 中文 */
const SYSTEM_COMMAND_PHRASES: Record<string, string> = {
  ls: '列出目录',
  cat: '查看文件',
  head: '查看文件头',
  tail: '查看文件尾',
  find: '查找文件',
  grep: '搜索内容',
  awk: '文本处理',
  sed: '文本替换',
  jq: '处理数据',
  wc: '统计行数',
  sort: '排序',
  uniq: '去重',
  git: 'Git 操作',
  curl: 'HTTP 请求',
  wget: '下载文件',
  python: '运行脚本',
  python3: '运行脚本',
  node: '运行脚本',
  pnpm: '包管理',
  npm: '包管理',
  pip: '包管理',
  docker: 'Docker 操作',
  echo: '打印输出',
  mkdir: '创建目录',
  rm: '删除文件',
  cp: '复制文件',
  mv: '移动文件',
  ssh: '远程连接',
  scp: '远程传输'
}

/**
 * 从 exec/bash 的 command 参数解析"执行了什么"的白话短语。
 *   1. 优先匹配 `xhey <sub>`（支持中间夹 --query 等参数）
 *   2. 退回到首个系统命令 token 的中文映射
 *   3. 都没命中 → undefined
 */
function detailExecIntent(args: unknown): string | undefined {
  const rec = asRecord(args)
  if (!rec) return undefined
  const raw = rec.command ?? rec.cmd ?? rec.script
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined

  // 剥掉引号字面量，防止 jmespath 表达式里的关键词误匹配
  const stripped = trimmed.replace(/'[^']*'/g, ' ').replace(/"[^"]*"/g, ' ')

  const xheyIdx = stripped.search(/\bxhey\b/)
  if (xheyIdx !== -1) {
    let bestSub: string | null = null
    let bestIdx = Number.POSITIVE_INFINITY
    for (const sub of KNOWN_XHEY_SUBS) {
      const re = new RegExp(`\\b${sub}\\b`)
      const m = stripped.slice(xheyIdx).search(re)
      if (m !== -1 && m < bestIdx) {
        bestIdx = m
        bestSub = sub
      }
    }
    if (bestSub) {
      return XHEY_SUBCOMMAND_PHRASES[bestSub]
    }
  }

  const firstToken = trimmed.split(/\s+/)[0]?.toLowerCase() ?? ''
  const cleanBin = firstToken.split('/').pop() ?? firstToken
  if (SYSTEM_COMMAND_PHRASES[cleanBin]) {
    return SYSTEM_COMMAND_PHRASES[cleanBin]
  }

  return undefined
}

/** 从 args 的 pattern/query 字段提取关键词 */
function detailPatternValue(args: unknown): string | undefined {
  const rec = asRecord(args)
  if (!rec) return undefined
  const raw = rec.pattern ?? rec.query ?? rec.q ?? rec.keyword
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed
}

/** 从 args.name 取技能 display_name；cache 未命中时退回 raw slug */
function detailSkillName(args: unknown): string | undefined {
  const rec = asRecord(args)
  if (!rec) return undefined
  const raw = rec.name
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  return getSkillDisplayName(trimmed) ?? trimmed
}

/* ──────────────── 入口 ──────────────── */

export interface ToolDisplay {
  label: string
  detail?: string
}

export function resolveToolDisplay(
  toolName: string,
  args?: unknown
): ToolDisplay {
  // 上下文感知：read SKILL.md → "加载技能 · {display_name 或 slug}"
  const skillLoad = detectSkillLoad(args)
  if (skillLoad) {
    return {
      label: '加载技能',
      detail: getSkillDisplayName(skillLoad.slug) ?? skillLoad.slug
    }
  }

  const label = (toolName && toolName.trim()) || '工具调用'

  // 通用 detail 探测：按 args 字段形态选择
  const detail =
    detailExecIntent(args) ??
    detailFileBasename(args) ??
    detailPatternValue(args) ??
    (label === '加载技能' ? detailSkillName(args) : undefined)

  return { label, detail }
}

/* ──────────────── 命令展开预览 ──────────────── */

/** 替换内部 env 变量名为用户友好占位 */
function humanizeCommand(cmd: string): string {
  return cmd
    .replace(/"?\$XHEY_USER_ID"?/g, '[当前用户]')
    .replace(/"?\$XHEY_GROUP_ID"?/g, '[当前团队]')
    .replace(/"?\$XHEY_COOKIE"?/g, '[凭证]')
}

/**
 * 提取工具调用的"真实命令"用于点 status badge 展开后显示。
 *   - 加载技能：args.name（slug）
 *   - bash/exec：args.command（替换内部 env 变量）
 *   - 读取文件：args.file_path / args.path
 *   - 通用 fallback：JSON.stringify
 */
export function extractCommandPreview(
  args: unknown,
  toolName?: string
): string | null {
  if (!args || typeof args !== 'object') return null
  const a = args as Record<string, unknown>

  if (toolName === '加载技能') {
    return typeof a.name === 'string' ? a.name : null
  }
  if (typeof a.command === 'string') return humanizeCommand(a.command)
  if (typeof a.file_path === 'string') return a.file_path
  if (typeof a.path === 'string') return a.path
  try {
    return JSON.stringify(args, null, 2)
  } catch {
    return null
  }
}

/**
 * 检测是否是"加载内部规则 skill"（xhey-shared/SKILL.md），
 * 这种调用对用户没有展示价值，应整条隐藏。
 */
export function isInternalSkillLoad(args: unknown): boolean {
  if (!args || typeof args !== 'object') return false
  const a = args as Record<string, unknown>
  const path = a.file_path ?? a.path ?? a.filepath ?? a.filename
  if (typeof path !== 'string') return false
  return path.includes('xhey-shared/SKILL.md')
}

/** 命令兜底片段:命令没命中已知白话短语时, 取 humanize 后的命令开头一小段 */
function detailCommandSnippet(args: unknown): string | undefined {
  const rec = asRecord(args)
  if (!rec) return undefined
  const raw = rec.command ?? rec.cmd ?? rec.script
  if (typeof raw !== 'string') return undefined
  const t = humanizeCommand(raw.trim())
  if (!t) return undefined
  return t.length > 28 ? `${t.slice(0, 28)}…` : t
}

/**
 * 工具行「xxx(参数)」括号里的参数提示 —— 取最能说明"在干嘛"的那个 arg.
 * 优先级: 文件名 → 命令白话意图 → 搜索关键词 → 命令片段兜底.
 *   读取表格 → "黄泥龙…xlsx";  执行命令 → "查询照片" / "jq -r '[.photos…'".
 */
/** 中间省略: 长文件名/串保留头尾(尾部含扩展名), 中间用 … 代替 */
function middleEllipsis(s: string, max = 22): string {
  if (s.length <= max) return s
  const head = Math.ceil((max - 1) / 2)
  const tail = max - 1 - head
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`
}

export function extractToolArgHint(args: unknown): string | undefined {
  const hint =
    detailFileBasename(args) ??
    detailExecIntent(args) ??
    detailPatternValue(args) ??
    detailCommandSnippet(args)
  if (!hint) return undefined
  // 括号里不喧宾夺主: 过长(如几十字符的文件名)中间省略, 头尾都看得到
  return middleEllipsis(hint)
}
